/**
 * Surface primitives — flat, neutral panels (the "Quiet Gallery" system).
 * Named Glass.tsx for import stability, but surfaces are now flat: chrome is
 * chromatically silent so photos stay the hero. Real glass blur lives only on
 * floating chrome (nav, lightbox) via the .glass-chrome/.glass-dark utilities.
 */
import type { ReactNode, HTMLAttributes } from 'react';
import { cn } from '../../lib/cn.ts';

interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** `strong` adds a heavier shadow for modal-like surfaces. */
  variant?: 'default' | 'strong';
}

export function GlassPanel({ children, variant = 'default', className, ...rest }: GlassPanelProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-line bg-surface',
        variant === 'strong' ? 'shadow-md' : 'shadow-sm',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

interface GlassCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  interactive?: boolean;
}

export function GlassCard({ children, interactive, className, ...rest }: GlassCardProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-line bg-surface shadow-sm',
        interactive &&
          'cursor-pointer transition duration-200 ease-standard hover:-translate-y-0.5 hover:shadow-lg',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
